/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useSignOut } from "../auth/useSignOut";
import { ThemeToggle } from "./ui/ThemeToggle";
import { cn } from "../lib/utils";

/**
 * The signed-in human, in one place: identity, roles, appearance, sign-out.
 * Opens upward from the sidebar rail and downward from the mobile top bar.
 */
export function UserMenu({ placement = "up" }: { placement?: "up" | "down" }) {
  const { session } = useAuth();
  const signOut = useSignOut();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (session.state !== "authenticated") {
    return null;
  }
  const { email, roles } = session.user;
  const initial = email.charAt(0).toUpperCase();

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        className={cn(
          "grid size-9 cursor-pointer place-items-center rounded-full text-sm font-semibold",
          "bg-accent/15 text-accent transition-all duration-150",
          "hover:bg-accent/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
          open && "ring-2 ring-accent/40",
        )}
      >
        {initial}
      </button>

      {open && (
        <div
          style={{ "--pop-y": placement === "up" ? "6px" : "-6px" } as React.CSSProperties}
          className={cn(
            "menu-pop absolute z-50 w-64 rounded-2xl border border-border bg-bg-elevated p-2 shadow-elevated",
            placement === "up" ? "bottom-full left-0 mb-2" : "right-0 top-full mt-2",
          )}
        >
          <div className="px-3 pb-3 pt-2">
            <div className="truncate text-sm font-medium text-text-primary" title={email}>
              {email}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {roles.map((role) => (
                <span
                  key={role}
                  className="rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
          <div className="border-t border-border px-3 py-3">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-text-tertiary">
              Appearance
            </div>
            <ThemeToggle />
          </div>
          <div className="border-t border-border pt-2">
            <button
              onClick={signOut}
              className="flex w-full cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-error-bg hover:text-error"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
